import { getBytes, ref } from 'firebase/storage';
import { storage } from '../firebase';
import type {
  CaptionCue,
  CaptionStyle,
  TimedTextAsset,
  TimedTextTrackRef,
} from '../types/timedText.types';

const MAX_TIMED_TEXT_BYTES = 25 * 1024 * 1024;
const cache = new Map<string, TimedTextAsset>();

export function clearTimedTextCache(): void {
  cache.clear();
}

export async function loadTimedTextAsset(track: TimedTextTrackRef, style: CaptionStyle): Promise<TimedTextAsset> {
  const cached = cache.get(track.asset.sha256);
  if (cached) return cached;

  const bytes = await getBytes(ref(storage, track.asset.storagePath), MAX_TIMED_TEXT_BYTES);
  if (bytes.byteLength > MAX_TIMED_TEXT_BYTES) throw new Error('TIMED_TEXT_SIZE_LIMIT');
  const decoded = track.asset.contentEncoding === 'gzip'
    ? await gunzip(bytes)
    : new Uint8Array(bytes);
  if (decoded.byteLength > MAX_TIMED_TEXT_BYTES) throw new Error('TIMED_TEXT_SIZE_LIMIT');
  const hash = await sha256Hex(decoded);
  if (hash !== track.asset.sha256) throw new Error('TIMED_TEXT_HASH_MISMATCH');
  const json = new TextDecoder('utf-8', { fatal: true }).decode(decoded);
  const asset = JSON.parse(json) as TimedTextAsset;
  validateTimedTextAsset(asset, track, style);
  cache.set(track.asset.sha256, asset);
  return asset;
}

export function activeCue(cues: CaptionCue[], segmentPositionMs: number): CaptionCue | null {
  let low = 0;
  let high = cues.length - 1;
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const cue = cues[mid];
    if (segmentPositionMs < cue.startMs) high = mid - 1;
    else if (segmentPositionMs >= cue.endMs) low = mid + 1;
    else return cue;
  }
  return null;
}

export function activeTokenIndex(cue: CaptionCue, segmentPositionMs: number): number {
  return cue.tokens.findIndex((token) => segmentPositionMs >= token.startMs && segmentPositionMs < token.endMs);
}

export function captionLineCapacityEm(style: CaptionStyle): number {
  const usableWidth = Math.max(0.4, 1 - style.horizontalMargin * 2);
  const fontSizeAsWidth = 0.07 * style.fontScale;
  return Math.max(2.5, usableWidth / fontSizeAsWidth - 0.96 - style.outlineWidth * 0.08);
}

export function captionTextWidthEm(text: string, style: CaptionStyle): number {
  const family = style.fontFamily === 'guidera-rounded'
    ? 1.04
    : style.fontFamily === 'guidera-serif'
      ? 1.03
      : style.fontFamily === 'guidera-mono'
        ? 1.1
        : 1;
  const weight = style.fontWeight >= 700 ? 1.035 : style.fontWeight >= 600 ? 1.02 : 1;
  return [...text].reduce((sum, char) => sum + glyphWidth(char), 0) * family * weight;
}

export function captionLineCount(text: string, style: CaptionStyle): number {
  const capacity = captionLineCapacityEm(style);
  const pieces = text.trim().match(/\s+|[^\s]+/gu) ?? [];
  if (pieces.length === 0) return 0;
  let lines = 1;
  let width = 0;
  let pendingWhitespace = 0;
  for (const piece of pieces) {
    if (/^\s+$/u.test(piece)) {
      pendingWhitespace += captionTextWidthEm(piece, style);
      continue;
    }
    const pieceWidth = captionTextWidthEm(piece, style);
    const requestedWidth = width > 0 ? pendingWhitespace + pieceWidth : pieceWidth;
    if (width > 0 && width + requestedWidth > capacity) {
      lines += 1;
      width = 0;
    }
    if (pieceWidth > capacity) {
      const occupied = Math.max(1, Math.ceil(pieceWidth / capacity));
      lines += occupied - 1;
      width = pieceWidth % capacity;
    } else {
      width += width > 0 ? pendingWhitespace + pieceWidth : pieceWidth;
    }
    pendingWhitespace = 0;
  }
  return lines;
}

function validateTimedTextAsset(asset: TimedTextAsset, track: TimedTextTrackRef, style: CaptionStyle): void {
  if (
    asset.schemaVersion !== 1 ||
    asset.trackId !== track.trackId ||
    asset.trackSetId !== track.trackSetId ||
    asset.revisionId !== track.revisionId ||
    asset.audioSegmentId !== track.audioSegmentId ||
    asset.languageTag !== track.languageTag ||
    asset.kind !== track.kind ||
    asset.timingMode !== track.timingMode ||
    asset.styleId !== track.styleId
  ) {
    throw new Error('TIMED_TEXT_BINDING_INVALID');
  }
  if (asset.sourceAudio.trimEndMs - asset.sourceAudio.trimStartMs !== asset.sourceAudio.durationMs) {
    throw new Error('TIMED_TEXT_TIMING_INVALID');
  }
  let lastEnd = -1;
  for (const cue of asset.cues) {
    if (cue.startMs < 0 || cue.startMs >= cue.endMs || cue.endMs > asset.sourceAudio.durationMs || cue.startMs < lastEnd) {
      throw new Error('TIMED_TEXT_TIMING_INVALID');
    }
    lastEnd = cue.endMs;
    if (captionLineCount(cue.text, style) > style.maxLines) throw new Error('TIMED_TEXT_STYLE_INVALID');
    if (cue.tokens.length > 0) {
      const reconstructed = cue.tokens.map((token) => `${token.separatorBefore}${token.text}`).join('');
      if (reconstructed !== cue.text) throw new Error('TIMED_TEXT_TIMING_INVALID');
    }
  }
}

async function gunzip(bytes: ArrayBuffer): Promise<Uint8Array> {
  if (typeof DecompressionStream === 'undefined') throw new Error('TIMED_TEXT_ENCODING_INVALID');
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'));
  const decoded = await new Response(stream).arrayBuffer();
  return new Uint8Array(decoded);
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

function glyphWidth(character: string): number {
  if (/\p{Mark}/u.test(character)) return 0;
  if (/\s/u.test(character)) return 0.32;
  if (/\p{Script=Han}|\p{Script=Hiragana}|\p{Script=Katakana}|\p{Script=Hangul}/u.test(character)) return 1;
  if (/\p{Extended_Pictographic}/u.test(character)) return 1.08;
  if (/[MW@%&]/u.test(character)) return 0.86;
  if (/[A-Z]/u.test(character)) return 0.66;
  if (/[0-9]/u.test(character)) return 0.58;
  if (/\p{Punctuation}|\p{Symbol}/u.test(character)) return 0.36;
  return 0.55;
}
